import { ModalProps, createUseModal, useModal } from "@imminently/react/context/modals";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@imminently/react/ui/core/dialog";
import { Text } from "@imminently/react/ui/core/text";
import { Button, IconButton, Stack, TextField, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { Icon } from "@/components/Icon";
import { encounterService } from "@/services";
import type { PatientEncounter } from "@/resources/types";
import { useState, useEffect } from "react";

type CommentSeverity = "low" | "medium" | "high";

const EncounterCommentModal = ({ props }: ModalProps<{ encounter: PatientEncounter }>) => {
  const encounter = props.encounter;
  const { mutateAsync: updateEncounter } = encounterService.useUpdate();
  const { popModal: close } = useModal();
  const [value, setValue] = useState(encounter.comment?.text ?? "");
  const [severity, setSeverity] = useState<CommentSeverity>((encounter.comment?.severity as CommentSeverity) ?? "low");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setValue(encounter.comment?.text ?? "");
    setSeverity((encounter.comment?.severity as CommentSeverity) ?? "low");
  }, [encounter.comment]);

  const hasComment = !!(encounter.comment?.text?.trim());

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateEncounter({
        id: encounter.id,
        payload: (value.trim() ? { comment: { text: value.trim(), severity } } : { comment: "" }) as unknown as Partial<PatientEncounter>,
      });
      close();
    } catch (error: unknown) {
      console.error("Failed to update encounter comment", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async () => {
    setIsSaving(true);
    try {
      await updateEncounter({
        id: encounter.id,
        payload: { comment: "" } as unknown as Partial<PatientEncounter>,
      });
      close();
    } catch (error: unknown) {
      console.error("Failed to remove encounter comment", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Text variant="h3">{hasComment ? "Edit encounter comment" : "Add encounter comment"}</Text>
        <IconButton onClick={close} size="small">
          <Icon icon="close" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="Enter a comment for this patient..."
            value={value}
            onChange={(e) => setValue(e.target.value)}
            variant="outlined"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Severity</InputLabel>
            <Select
              value={severity}
              label="Severity"
              onChange={(e) => setSeverity(e.target.value as CommentSeverity)}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {hasComment && (
          <Button color="error" onClick={handleRemove} disabled={isSaving}>
            Remove
          </Button>
        )}
        <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
          <Button onClick={close} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export const useEncounterCommentModal = createUseModal(EncounterCommentModal);
